import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TranslatorPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Language {
  code: string;
  name: string;
}

function TranslatorPage() {
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('en');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [additionalInstruction, setAdditionalInstruction] = useState('');

  useEffect(() => {
    // Load available languages
    axios.get(`${API_URL}/api/languages`)
      .then(response => {
        const langs = Object.entries(response.data.languages).map(([code, name]) => ({
          code,
          name: name as string
        }));
        setLanguages(langs);
      })
      .catch(err => {
        console.error('Failed to load languages:', err);
      });
  }, []);

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      setError('Bitte geben Sie einen Text ein');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/translate`, {
        text: sourceText,
        source_lang: sourceLang,
        target_lang: targetLang,
        additional_instruction: additionalInstruction
      });

      setTranslatedText(response.data.translated_text);
      setCharCount(response.data.char_count);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Übersetzung fehlgeschlagen');
      console.error('Translation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwapLanguages = () => {
    // Don't swap if source is auto-detect
    if (sourceLang === 'auto') {
      return;
    }
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setSourceText(translatedText);
    setTranslatedText(sourceText);
  };

  const handleClear = () => {
    setSourceText('');
    setTranslatedText('');
    setError('');
    setCharCount(0);
    setAdditionalInstruction('');
  };

  return (
    <div className="translator-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <h1 className="logo">Translation Service</h1>
            <a href="/stats" className="stats-link">Statistiken</a>
          </div>
        </div>
      </header>

      {/* Main Translator */}
      <main className="main-content">
        <div className="container">
          <div className="translator-container">
            {/* Language Selector Bar */}
            <div className="language-bar">
              <div className="language-selector">
                <select
                  value={sourceLang}
                  onChange={(e) => setSourceLang(e.target.value)}
                  className="language-select"
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="swap-button"
                onClick={handleSwapLanguages}
                aria-label="Sprachen tauschen"
              >
                ⇄
              </button>

              <div className="language-selector">
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  className="language-select"
                >
                  {languages.filter(lang => lang.code !== 'auto').map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Translation Panels */}
            <div className="translation-panels">
              {/* Source Panel */}
              <div className="panel source-panel">
                <textarea
                  className="text-area"
                  placeholder="Text eingeben..."
                  value={sourceText}
                  onChange={(e) => setSourceText(e.target.value)}
                  disabled={isLoading}
                />
                <div className="panel-footer">
                  <span className="char-count">
                    {sourceText.length} Zeichen
                  </span>
                  {sourceText && (
                    <button
                      className="clear-button"
                      onClick={handleClear}
                    >
                      Löschen
                    </button>
                  )}
                </div>
              </div>

              {/* Target Panel */}
              <div className="panel target-panel">
                <div className="text-area translated-text">
                  {isLoading ? (
                    <div className="loading">
                      <div className="loading-spinner"></div>
                      <p>Übersetze...</p>
                    </div>
                  ) : translatedText ? (
                    translatedText
                  ) : (
                    <span className="placeholder">Übersetzung erscheint hier</span>
                  )}
                </div>
                <div className="panel-footer">
                  {translatedText && (
                    <span className="char-count text-muted">
                      {translatedText.length} Zeichen
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Additional Instruction Input */}
            <div className="instruction-bar">
              <input
                type="text"
                className="instruction-input"
                placeholder='Optional: Zusatzanweisung (z.B. "freundlicher", "förmlicher", "als E-Mail")'
                value={additionalInstruction}
                onChange={(e) => setAdditionalInstruction(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Translate Button */}
            <div className="action-bar">
              <button
                className="translate-button"
                onClick={handleTranslate}
                disabled={isLoading || !sourceText.trim()}
              >
                {isLoading ? 'Übersetze...' : 'Übersetzen'}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="text-muted">
            Powered by Azure OpenAI • Intranet Translation Service
          </p>
        </div>
      </footer>
    </div>
  );
}

export default TranslatorPage;
